// src/scene/SceneAnimation.ts
import { useLayoutEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import * as THREE from 'three'

gsap.registerPlugin(ScrollTrigger)

export function useSceneAnimation(
  enabled: boolean,
  containerRef: any,
  modelRef: any,
  cameraRef: any,
  controlsRef: any,
  isMobile: boolean,
  fabricRef: React.RefObject<THREE.Mesh>
) {
  useLayoutEffect(() => {
    // Sécurité initiale
    if (
      !enabled ||
      !containerRef.current ||
      !modelRef.current ||
      !cameraRef.current ||
      !controlsRef.current
    ) return

    // ---------- Helper pour bounding box ----------
    let boxHelper: THREE.BoxHelper | null = null
    
    const showBoundingBox = () => {
      if (fabricRef?.current && !boxHelper && cameraRef.current) {
        boxHelper = new THREE.BoxHelper(fabricRef.current, 0xff0000)
        cameraRef.current.add(boxHelper)
      }
    }

    const hideBoundingBox = () => {
      if (boxHelper && cameraRef.current) {
        cameraRef.current.remove(boxHelper)
        boxHelper = null
      }
    }

    // ---------- Boucle de synchronisation (Anti-Décalage) ----------
    // On force la mise à jour des contrôles 60 fois par seconde
    const syncControls = () => {
      if (controlsRef.current) {
        controlsRef.current.update()
      }
    }
    gsap.ticker.add(syncControls)

    // ---------- GSAP Context ----------
    const ctx = gsap.context(() => {
      
      // 1. Initialisation forcée (Point de départ Step 1)
      gsap.set(cameraRef.current.position, { x: 4, y: 2, z: 2 })
      gsap.set(controlsRef.current.target, { 
        x: isMobile ? 1 : 0, 
        y: isMobile ? 1 : 1.5, 
        z: 0 
      })

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1.5,
          invalidateOnRefresh: true
        },
        // On force aussi l'update pendant le scroll pour plus de fluidité
        onUpdate: syncControls
      })

      // --- STEP 2 : Rotation vers la Droite ---
      tl.to(cameraRef.current.position, {
        x: isMobile ? 2 : 6, y: 2, z: 3, 
        duration: 1,
        overwrite: 'auto'
      })
      .to(controlsRef.current.target, { 
        x: -1, y: 1, z: 0, 
        duration: 1,
        overwrite: 'auto'
      }, '<')

      // --- STEP 3 : FRONT (Vue Tissu & Défauts) ---
      .to(cameraRef.current.position, { 
        x: -1, y: 2, z: isMobile ? 0 : -1, 
        duration: 1,
        overwrite: 'auto'
      })
      .to(controlsRef.current.target, { 
        x: -1, y: 2, z: -2, 
        duration: 1,
        overwrite: 'auto'
      }, '<')
      .to(modelRef.current.rotation, { 
        y: 0, 
        duration: 1,
        overwrite: 'auto'
      }, '<')
      
      // Gestion Bounding Box
      .call(() => { if (fabricRef?.current) showBoundingBox() })
      .to({}, { duration: 2.5 }) // Pause sur le plan Front
      .call(() => hideBoundingBox())

      // --- STEP 4 : CONFIGURATEUR (Vue finale) ---
      .to(cameraRef.current.position, { 
        x: isMobile ? 0 : -4, 
        y: isMobile ? 3 : 3.5, 
        z: isMobile ? 3 : 4, 
        duration: 1,
        overwrite: 'auto'
      })
      .to(modelRef.current.rotation, { 
        y: Math.PI / 6, 
        duration: 1,
        overwrite: 'auto'
      }, '<')
      .to(controlsRef.current.target, { 
        x: 0, y: 0.5, z: 0, 
        duration: 1,
        overwrite: 'auto'
      }, '<')

    }, containerRef)

    // Petit délai pour laisser le DOM se stabiliser
    const timer = setTimeout(() => ScrollTrigger.refresh(), 500)

    // ---------- Cleanup ----------
    return () => {
      clearTimeout(timer)
      gsap.ticker.remove(syncControls)
      hideBoundingBox()
      ctx.revert()
    }
  }, [enabled, isMobile]) 
  // Note: On ne met pas fabricRef ici pour éviter de reset la timeline au clic

  return null
}